import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../../assets/images";
import { useTranslate } from "../../../context/TranslationContext";
import {
  useGetSaveforlaterQuery,
  useHandleSaveforlaterMutation,
  useHandleWishlistMutation,
  userApiService,
} from "../../../redux/Apis/User";
import {
  convertPrice,
  formatCurrency,
  getConfigByCountryCode,
} from "../../../utils/localization";

const CourseCard = ({ data }) => {
  const { t } = useTranslate("courseCard");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authSlice);
  const isMobile = window.innerWidth <= 576;
  const { data: saveForLaterData } = useGetSaveforlaterQuery(
    { lang },
    {
      skip: !user,
    }
  );
  const [
    handleWishlist,
    { isLoading: isWishlistLoading, isSuccess, isError, error },
  ] = useHandleWishlistMutation();
  const [
    handleSaveForLater,
    {
      isLoading: isSaveForLaterLoading,
      isSuccess: isSuccessSaveForLater,
      isError: isErrorSaveForLater,
      error: errorSaveForLater,
    },
  ] = useHandleSaveforlaterMutation();
  const isInWishlist = useMemo(
    () => user?.course_wishlist?.includes(data?._id) || false,
    [user, data?._id]
  );
  const isInSaveForLater = useMemo(
    () =>
      saveForLaterData?.data?.some((item) => item._id === data?._id) || false,
    [saveForLaterData, data?._id]
  );
  const img = data?.files?.[0]?.url?.startsWith("http")
    ? data?.files?.[0]?.url
    : images?.dummy?.course;

  const selectedCountry =
    user?.country || localStorage?.getItem("selectedCountry") || "gb";
  const [displayPrice, setDisplayPrice] = useState("");

  useEffect(() => {
    const updatePrice = async () => {
      if (!data?.price?.amount) return;
      const { currency } = getConfigByCountryCode(selectedCountry);
      const converted = await convertPrice(
        data.price.amount,
        data.price.currency,
        currency
      );
      setDisplayPrice(formatCurrency(converted, selectedCountry));
    };
    updatePrice();
  }, [data?.price?.amount, data?.price?.currency, selectedCountry]);

  useEffect(() => {
    if (isSuccess) toast.success(t("wishlist_success"));
    if (isError) toast.error(error?.data?.message || t("wishlist_error"));
  }, [isSuccess, isError, error]);

  useEffect(() => {
    if (isSuccessSaveForLater) toast.success(t("saveforlater_success"));
    if (isErrorSaveForLater)
      toast.error(errorSaveForLater?.data?.message || t("saveforlater_error"));
  }, [isSuccessSaveForLater, isErrorSaveForLater, errorSaveForLater]);

  const onClickCard = () => {
    if (data?.hasOwnProperty("progress")) navigate(`/lecture/${data._id}`);
    else navigate(`/class-detail/${data._id}`);
  };

  const addToWishlist = async (e) => {
    e.stopPropagation();

    if (!user) {
      toast.info(
        t("login_to_add_wishlist") ||
          "Please log in to add this to your wishlist"
      );
      return;
    }

    const isAdding = !isInWishlist;

    dispatch(
      userApiService.util.updateQueryData("getWishlist", undefined, (draft) => {
        draft.data = isAdding
          ? [...(draft.data || []), data]
          : draft.data.filter((item) => item._id !== data._id);
      })
    );

    try {
      await handleWishlist({ id: data._id, params: { lang } }).unwrap();
    } catch (err) {
      dispatch(
        userApiService.util.updateQueryData(
          "getWishlist",
          undefined,
          (draft) => {
            draft.data = isAdding
              ? draft.data.filter((item) => item._id !== data._id)
              : [...(draft.data || []), data];
          }
        )
      );
      toast.error(err?.data?.message || t("wishlist_error"));
    }
  };

  const addToSaveForLater = async (e) => {
    e.stopPropagation();

    if (!user) {
      toast.info(
        t("login_to_add_mylist") || "Please log in to add this to your list"
      );
      return;
    }

    dispatch(
      userApiService.util.updateQueryData(
        "getSaveforlater",
        undefined,
        (draft) => {
          draft.data = isInSaveForLater
            ? draft.data.filter((item) => item._id !== data._id)
            : [...(draft.data || []), data];
        }
      )
    );

    try {
      const courseId = data._id;
      await handleSaveForLater({
        courseId,
        params: { lang },
      }).unwrap();
      dispatch(
        userApiService.endpoints.getSaveforlater.initiate(undefined, {
          forceRefetch: true,
        })
      );
    } catch (err) {
      dispatch(
        userApiService.util.updateQueryData(
          "getSaveforlater",
          undefined,
          (draft) => {
            draft.data = isInSaveForLater
              ? [...(draft.data || []), data]
              : draft.data.filter((item) => item._id !== data._id);
          }
        )
      );
      toast.error(t("saveforlater_error"));
    }
  };

  const onEdit = (e) => {
    e.stopPropagation();
    navigate(`/course/details/${data?._id}`);
  };

  return (
    <div className="category-card" onClick={onClickCard}>
      {user?.role !== "creator" && (
        <div
          className="dropdown category-card-dropdown"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="category-card-dropdown-btn white-text"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            disabled={isWishlistLoading || isSaveForLaterLoading}
            style={{
              padding: isMobile && "2px 4px",
              fontSize: isMobile && "10px",
              height: isMobile ? "22px" : "32px",
              width: isMobile ? "22px" : "32px",
            }}
          >
            {isWishlistLoading || isSaveForLaterLoading ? (
              <i
                className="fas fa-spinner fa-spin"
                style={{ fontSize: isMobile ? "10px" : "14px" }}
              />
            ) : (
              <i
                className="fas fa-ellipsis-v"
                style={{ fontSize: isMobile ? "10px" : "16px" }}
              />
            )}
          </button>

          <ul
            className="dropdown-menu dropdown-menu-end"
            data-bs-display="static"
            data-bs-offset="0,0"
            data-bs-reference="parent"
            data-bs-popper="none"
            style={{
              position: "absolute",
              inset: "auto 0 auto auto",
              minWidth: "unset",
              maxWidth: "100%",
              width: "auto",
              padding: isMobile ? "4px" : "8px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              zIndex: 9999,
            }}
          >
            <li className="px-2">
              <h6
                className="white-text p-md"
                style={{
                  fontSize: isMobile ? "8px" : "14px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {t("lists")}
              </h6>
            </li>

            <hr className="my-1" />

            {user && data?.created_by?._id === user?._id ? (
              <li>
                <a
                  className="dropdown-item d-flex gap-2 align-items-center"
                  onClick={onEdit}
                >
                  <i className="fas fa-pen" /> {t("edit")}
                </a>
              </li>
            ) : (
              <>
                <li>
                  <a
                    className="dropdown-item d-flex gap-2 align-items-center"
                    onClick={addToWishlist}
                    style={{
                      fontSize: isMobile ? "10px" : "14px",
                      padding: isMobile ? "4px 6px" : "8px 10px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <i
                      className="fas fa-heart"
                      style={{ fontSize: isMobile ? "10px" : "14px" }}
                    />
                    {isInWishlist
                      ? t("remove_from_wishlist")
                      : t("add_to_wishlist")}
                  </a>
                </li>

                <li>
                  <a
                    className="dropdown-item d-flex gap-2 align-items-center"
                    onClick={addToSaveForLater}
                    style={{
                      fontSize: isMobile ? "10px" : "14px",
                      padding: isMobile ? "4px 6px" : "8px 10px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <i
                      className="fas fa-save"
                      style={{ fontSize: isMobile ? "10px" : "14px" }}
                    />
                    {isInSaveForLater
                      ? t("remove_from_mylist")
                      : t("add_to_mylist")}
                  </a>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
      {user && data?.created_by?._id === user?._id && (
        <div
          style={{
            background: "rgba(0,0,0,0.5)",
            position: "absolute",
            borderRadius: "4px",
            top: "10px",
            left: "10px",
          }}
          className="px-3"
        >
          <p className="m-0 text-capitalize p-sm">{data?.status}</p>
        </div>
      )}
      <img
        src={img || images?.dummy?.course}
        alt=""
        className="img-fluid category-card-img"
      />
      <div className="category-card-inner">
        <h6
          className="text-uppercase m-0"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            lineHeight: "1.2",
          }}
        >
          {data?.title}
        </h6>

        <p
          className="l-grey-text p-sm m-0 mt-2"
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
            lineHeight: "1.2",
          }}
        >
          {data?.headline ? data?.headline : data?.learning_points.join(", ")}
        </p>
        {data?.price?.amount && (
          <p className="text-end semi-bold m-purple-text mb-0">
            {displayPrice && (
              <p className="text-end semi-bold m-purple-text mb-0">
                {displayPrice}
              </p>
            )}
          </p>
        )}
        {data?.hasOwnProperty("progress") && (
          <>
            <div
              className="progress"
              role="progressbar"
              aria-valuenow={data?.progress || 0}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="progress-bar"
                style={{ width: `${data?.progress || "0"}%` }}
              />
            </div>
            <div className="d-flex mt-2 gap-2">
              <div className="flex-grow-1">
                <p className="p-xs mb-0">
                  {data?.progress || "0"}% {t("complete")}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(CourseCard);
